import React, { useCallback, useRef, useState } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import { FormattedMessage, useIntl, defineMessages } from "react-intl";
import { Tip } from "./Tip";
import { ToastTip } from "./ToastTip";
import { useEffect } from "react";
import { ReactComponent as DesktopControlsIcon } from "../icons/DesktopControls.svg";
import { ReactComponent as ShiftIcon } from "../icons/Shift.svg";
import { ReactComponent as KeyQIcon } from "../icons/Rotate_Left.svg";
import { ReactComponent as KeyEIcon } from "../icons/Rotate_Right.svg";
import { ReactComponent as LookAround } from "../icons/LMB.svg";
import { ReactComponent as RightClick } from "../icons/RMB.svg";
import { ReactComponent as TouchAndDrag } from "../icons/TouchAndDrag.svg";
import { ReactComponent as Joystick } from "../icons/Joystick.svg";
import { ReactComponent as AIcon } from "../icons/A.svg";
import { ReactComponent as WIcon } from "../icons/W.svg";
import { ReactComponent as SIcon } from "../icons/S.svg";
import { ReactComponent as DIcon } from "../icons/D.svg";
import { discordBridgesForPresences, hasEmbedPresences } from "../../utils/phoenix-utils";
import configs from "../../utils/configs";
import styles from "./Tip.scss";

import { FluentTips } from "./FluentTips";

// These keys are hardcoded in the input system to be based on the physical location on the keyboard rather than character
let moveKeys = "W A S D";
let turnLeftKey = "Q";
let turnRightKey = "E";

const WStyles = {
  marginTop: "-34px"
};

const SStyles = {
  marginLeft: "-33px"
};
// TODO The API to map from physical key to character is experimental. Depending on prospects of this getting wider
// implimentation we may want to cook up our own polyfill based on observing key inputs
if (window.navigator.keyboard !== undefined && window.navigator.keyboard.getLayoutMap) {
  window.navigator.keyboard
    .getLayoutMap()
    .then(function (map) {
      moveKeys = `${map.get("KeyW") || "W"} ${map.get("KeyA") || "A"} ${map.get("KeyS") || "S"} ${
        map.get("KeyD") || "D"
      }`.toUpperCase();
      turnLeftKey = map.get("KeyQ")?.toUpperCase();
      turnRightKey = map.get("KeyE")?.toUpperCase();
    })
    .catch(function (e) {
      // This occurs on Chrome 93 when the Hubs page is in an iframe
      console.warn(`Unable to remap keyboard: ${e}`);
    });
}

const headerMessages = {
  "tips.desktop.intro": {
    id: "tips.desktop.intro-title",
    header: "Hello! 👋",
    buttonStart: true,
    activeButtonLabel: {
      id: "tips.button.got-it",
      header: "Got it!"
    }
  },
  "tips.mobile.intro": {
    id: "tips.mobile.intro-title",
    header: "Hello! 👋",
    buttonStart: true,
    activeButtonLabel: {
      id: "tips.button.got-it",
      header: "Got it!"
    }
  },
  "tips.desktop.locomotion": {
    position: 1,
    alignmentBaseline: true
  },
  "tips.mobile.locomotion": {
    position: 2
  },
  "tips.desktop.turning": {
    position: 2
  },
  "tips.desktop.look": {
    position: 3
  },
  "tips.mobile.look": {
    position: 1
  },
  "tips.mobile.invite": {
    position: 3
  },
  "tips.desktop.teleport": {
    position: 4
  },
  "tips.desktop.end": {
    id: "tips.desktop.end-title",
    header: "Well Done! 👍",
    buttonStart: true,
    hideSkipButton: true,
    activeButtonLabel: {
      id: "tips.button.done",
      header: "Done"
    }
  },
  "tips.mobile.end": {
    id: "tips.mobile.end-title",
    header: "Well Done! 👍",
    buttonStart: true,
    hideSkipButton: true,
    activeButtonLabel: {
      id: "tips.button.done",
      header: "Done"
    }
  }
};

const desktopControlContent = () => {
  return (
    <div style={{ display: "flex", gap: "5px", marginTop: "30px" }}>
      <AIcon size={20} alt="A" />
      <WIcon style={WStyles} size={20} alt="W" />

      <SIcon style={SStyles} size={20} alt="S" />
      <DIcon alt="D" />
    </div>
  );
};

const desktopShiftContent = () => {
  return <ShiftIcon />;
};

const desktopQKeyContent = () => {
  return <KeyQIcon />;
};

const desktopEKeyContent = () => {
  return <KeyEIcon />;
};

const desktopLookAroundContent = () => {
  return <LookAround />;
};

const desktopTeleportContent = () => {
  return <RightClick />;
};

const mobileLookContent = () => {
  return <TouchAndDrag />;
};

const mobileLocomotionContent = () => {
  return (
    <div className={styles.joystickContainer} style={{ gap: "8px" }}>
      <Joystick />
      <Joystick />
    </div>
  );
};

const onboardingMessages = defineMessages({
  "tips.mobile.look": {
    id: "tips.mobile.look",
    defaultMessage: "Touch and drag to look around. {mobileLookContent}"
  },
  "tips.mobile.locomotion": {
    id: "tips.mobile.locomotion",
    defaultMessage: "Use the joystick to move. {mobileLocomotionContent}"
  },
  "tips.mobile.invite": {
    id: "tips.mobile.invite",
    defaultMessage: "Use the Invite button in the bottom left to share this room."
  },
  "tips.mobile.intro": {
    id: "tips.desktop.intro",
    defaultMessage:
      "Welcome to the {appName} metaverse. Let's take a quick tour of the main functionalities. Are you ready to start?"
  },
  "tips.desktop.intro": {
    id: "tips.desktop.intro",
    defaultMessage:
      "Welcome to the {appName} metaverse. Let's take a quick tour of the main functionalities. Are you ready to start?"
  },
  "tips.desktop.end": {
    id: "tips.desktop.end",
    defaultMessage: "Now you are ready to explore our universe"
  },
  "tips.mobile.end": {
    id: "tips.desktop.end",
    defaultMessage: "Now you are ready to explore our universe"
  },
  "tips.desktop.look": {
    id: "tips.desktop.look",
    defaultMessage: "Click and drag to look around. {desktopLookAroundContent}"
  },
  "tips.desktop.locomotion": {
    id: "tips.desktop.locomotion",
    defaultMessage: "{desktopControlContent} keys to move. Hold {desktopShiftContent} to boost."
  },
  "tips.desktop.turning": {
    id: "tips.desktop.turning",
    defaultMessage: "Perfect. Use the {desktopQKeyContent} and {desktopEKeyContent} keys to rotate."
  },
  "tips.desktop.invite": {
    id: "tips.desktop.invite",
    defaultMessage: "Nobody else is here. Use the invite button in the bottom left to share this room."
  },
  "tips.desktop.teleport": {
    id: "tips.desktop.teleport",
    defaultMessage: "Right click to teleport {desktopTeleportContent}"
  }
});

function OkDismissLabel() {
  return <FormattedMessage id="tips.dismiss.ok" defaultMessage="Ok" />;
}

export function FullscreenTip(props) {
  return (
    <FluentTips {...props} dismissLabel={<OkDismissLabel />}>
      <FormattedMessage id="tips.fullscreen" defaultMessage="Entered fullscreen mode. Press Escape to show UI." />
    </FluentTips>
  );
}

export function RecordModeTip() {
  return (
    <ToastTip>
      <FormattedMessage id="record-mode-enabled-tip" defaultMessage="Record mode on, press 'B' to toggle off" />
    </ToastTip>
  );
}

function SkipDismissLabel() {
  return <FormattedMessage id="tips.dismiss.skip" defaultMessage="Skip" />;
}

export function TipContainer({ inLobby = false, inRoom, isStreaming, isEmbedded, scene, store, hubId, presences }) {
  const [lobbyTipDismissed, setLobbyTipDismissed] = useState(false);
  const [broadcastTipDismissed, setBroadcastTipDismissed] = useState(() =>
    store.state.confirmedBroadcastedRooms.includes(hubId)
  );
  const [streamingTipDismissed, setStreamingTipDismissed] = useState(false);
  const [embeddedTipDismissed, setEmbeddedTipDismissed] = useState(false);
  const [onboardingTipId, setOnboardingTipId] = useState(null);
  const timeoutRef = useRef(null);

  const intl = useIntl();

  const onSkipOnboarding = useCallback(() => {
    scene.systems.tips.skipTips();
  }, [scene]);

  useEffect(() => {
    function onSceneTipChanged({ detail: tipId }) {
      setOnboardingTipId(null);
      setOnboardingTipId(tipId);
    }

    scene.addEventListener("tip-changed", onSceneTipChanged);

    setOnboardingTipId(scene.systems.tips.activeTip);
  }, [scene]);

  const discordBridges = presences ? discordBridgesForPresences(presences) : [];
  const isBroadcasting = discordBridges.length > 0;
  let activeStep = undefined;

  if (headerMessages[onboardingTipId] && headerMessages[onboardingTipId].position) {
    activeStep = headerMessages[onboardingTipId].position;
  }

  const onSetTipSelected = () => {
    if (activeStep) {
      scene.systems.tips.skipTip(activeStep);
    } else {
      scene.systems.tips.skipTip(0);
    }
  };

  const baseline = headerMessages[onboardingTipId] ? headerMessages[onboardingTipId].alignmentBaseline : undefined;

  if (inLobby) {
    if (lobbyTipDismissed) {
      return null;
    }

    return (
      <FluentTips onDismiss={() => setLobbyTipDismissed(true)} dismissLabel={<OkDismissLabel />}>
        <FormattedMessage id="tips.lobby" defaultMessage="You're in the lobby. Others cannot see or hear you." />
      </FluentTips>
    );
  } else if (inRoom) {
    if (onboardingTipId) {
      return (
        <FluentTips
          onDismiss={onSkipOnboarding}
          step={scene.systems.tips.getTips()}
          activeStep={activeStep}
          onSetTipSelected={onSetTipSelected}
          activeButtonLabel={
            headerMessages[onboardingTipId] ? headerMessages[onboardingTipId].activeButtonLabel : undefined
          }
          buttonStart={headerMessages[onboardingTipId] ? headerMessages[onboardingTipId].buttonStart : undefined}
          hideSkipButton={headerMessages[onboardingTipId] ? headerMessages[onboardingTipId].hideSkipButton : undefined}
          dismissLabel={<SkipDismissLabel />}
          header={
            <>
              {headerMessages[onboardingTipId].id && (
                <>
                  {intl.formatMessage({
                    id: headerMessages[onboardingTipId].id,
                    defaultMessage: headerMessages[onboardingTipId].header
                  })}
                </>
              )}
            </>
          }
          content={
            <>
              {intl.formatMessage(onboardingMessages[onboardingTipId], {
                appName: configs.translation("app-name"),
                moveKeys,
                desktopControlContent: desktopControlContent(),
                desktopShiftContent: desktopShiftContent(),
                desktopQKeyContent: desktopQKeyContent(),
                desktopEKeyContent: desktopEKeyContent(),
                desktopLookAroundContent: desktopLookAroundContent(),
                desktopTeleportContent: desktopTeleportContent(),
                mobileLookContent: mobileLookContent(),
                mobileLocomotionContent: mobileLocomotionContent(),
                turnLeftKey,
                turnRightKey
              })}
            </>
          }
        ></FluentTips>
      );
    }

    if (isStreaming && !streamingTipDismissed) {
      return (
        <FluentTips onDismiss={() => setStreamingTipDismissed(true)} dismissLabel={<OkDismissLabel />}>
          <FormattedMessage
            id="tips.streaming"
            defaultMessage="Now broadcasting to the lobby. Exit streamer mode in the add menu when you're done."
          />
        </FluentTips>
      );
    }

    if (isBroadcasting && !broadcastTipDismissed) {
      return (
        <FluentTips onDismiss={() => setBroadcastTipDismissed(true)} dismissLabel={<OkDismissLabel />}>
          <FormattedMessage
            id="tips.discord"
            defaultMessage="Chat in this room is being bridged to {broadcastTarget} on Discord."
            values={{ broadcastTarget: discordBridges.map(channelName => "#" + channelName).join(", ") }}
          />
        </FluentTips>
      );
    }

    if ((isEmbedded || hasEmbedPresences(presences)) && !embeddedTipDismissed) {
      return (
        <FluentTips onDismiss={() => setEmbeddedTipDismissed(true)} dismissLabel={<OkDismissLabel />}>
          <FormattedMessage
            id="tips.embedded"
            defaultMessage="This Verse is embedded, so it may be visible to visitors on other websites."
          />
        </FluentTips>
      );
    }

    return null;
  }

  return null;
}

TipContainer.propTypes = {
  inLobby: PropTypes.bool,
  inRoom: PropTypes.bool,
  isStreaming: PropTypes.bool,
  isEmbedded: PropTypes.bool,
  scene: PropTypes.object.isRequired,
  store: PropTypes.object.isRequired,
  hubId: PropTypes.string,
  presences: PropTypes.object
};
